import { Nav } from '@accedo/spatial-nav';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import ActiveRadioButton from '##/assets/icons/icon-Radio-Button-Selected.svg';
import RadioButton from '##/assets/icons/icon-Radio-Button.svg';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { nav } from '##/init';
import {
  defer,
  getHbbTvPlatformVersion,
  onBackPressOnPagesWithSideNav,
  safeAssignFocus,
} from '##/utils';
import {
  getUserInfo,
  isLoggedIn,
} from '##/services/authentication/Authentication';
import { config } from '##/services/store/store';
import { MenuItem } from '##/services/tenApi/system/types';
import { DeviceInformation, getDeviceInformation } from '##/utils/device';
import { PageNavigation } from '##/utils/PageNavigation';
import { useKeyPress } from '##/utils/hooks';
import { version } from '../../../package.json';
import { SecondaryNav, SecondaryNavItem } from '../SecondaryNav/SecondaryNav';

const REGION_NAV_LIST_ID = 'select-region-nav';

const styles = stylesheet`
@import '##/styles/import.scss';

.settings-page-container {
    width: 100vw;
    height: 100vh;
    display: flex;
    flex-shrink: 0;
    font-family: barlow;
}

.select-container {
  margin-top: pxToVh(63);
  margin-left: pxToVw(39);
  .header {
    font-size: pxToVw(22);
    margin-bottom: pxToVw(18);
  }
  .region-item {
    width: pxToVw(168);
    height: pxToVw(40);
    display:flex;
    flex-direction:row;
    align-items: center;
    justify-content: flex-start;
    border-radius: pxToVw(25);
    font-size: pxToVw(18);
    margin-bottom: pxToVw(14);
    .radio-button {
      margin-left: pxToVw(18);
      margin-right: pxToVw(14);
    }
    &:global(.current-focus) {
      background-color: $pure-white;
      color: $pure-black;
    }
  }
}
.static-page {
  padding-top: pxToVw(60);
  padding-left: pxToVw(39);
  white-space: pre-line;
  .information-header {
    font-size: pxToVw(18);
  }
  .page-label {
    font-size: pxToVw(22);
    margin-bottom: pxToVw(26);
  }
  .page-content {
    font-size: pxToVw(16);
    line-height: 1.3;
  }
}
`;

type SettingsNavState = undefined | { secondaryNavSlug: string };

const secondaryNavId = 'setting-secondary-nav';
const configItems = config.$get();

const getNavItems = (
  settingsNavItems: MenuItem[] | undefined,
): SecondaryNavItem[] => {
  if (settingsNavItems) {
    return settingsNavItems.map((item: MenuItem) => ({
      text: item.label,
      key: item.slug,
    }));
  }
  return [];
};

export const SettingsPage: FunctionalComponent<{}> = () => {
  const initialRegion = configItems.geo.region;
  const { systemName } = configItems.api;

  const { regions } = config.$get().app;
  const { email } = getUserInfo() || {};
  const [settingsData, setSettingsData] = useState<MenuItem>();
  const [activeRegion, setActiveRegion] = useState<string>(initialRegion);
  const [secondaryNavItems, setSecondaryNavItems] = useState<
    SecondaryNavItem[]
  >([]);
  const [currentNavSetting, setCurrentNavSetting] = useState<string>(
    (PageNavigation.getCurrentState() as SettingsNavState)?.secondaryNavSlug ===
      'faqs'
      ? 'faqs'
      : 'change-region',
  );
  const [deviceInformation, setDeviceInfomation] = useState<DeviceInformation>({
    model: '',
    deviceId: '',
    ipAddress: { ip: '', state: '' },
    platform: '',
    firmwareVersion: '',
    manufacturer: '',
  });

  const [appInformation, setAppInformation] = useState<string>('');

  const settingsNavInitialItems = configItems?.app?.menuConfig?.secondary?.find(
    (item) => item.slug === 'setting',
  )?.subItems;

  const appInfoItem = {
    apiEndpoint: '',
    description: appInformation,
    enabled: true,
    label: 'App Info',
    logoUrl: '',
    slug: 'app-info',
    subItems: [],
    type: 'Settings',
  };
  const settingsNavItems = settingsNavInitialItems && [
    ...settingsNavInitialItems,
    appInfoItem,
  ];
  const onRegionOptionSelect = (item: string): void => {
    setActiveRegion(item);
    config.$set({ geo: { region: item, allow: config.$get().geo.allow } });
    localStorage.setItem('region', item);
  };

  const getSettingsData = (option: string): MenuItem | undefined => {
    const navItems = settingsNavItems?.find((item) => item.slug === option);
    return navItems;
  };

  const onNavSelect = (key: string): void => {
    setCurrentNavSetting(key);
    const settingsData = getSettingsData(key);
    setSettingsData(settingsData);
    if (settingsData?.label) {
      PageNavigation.subPageRoute$.send(settingsData.label);
    }
  };

  useEffect(() => {
    getDeviceInformation().then((item) => {
      setDeviceInfomation(item);
    });
  }, []);

  useEffect(() => {
    if (secondaryNavItems.length) {
      const pageNavigationState: SettingsNavState = PageNavigation.getCurrentState();
      if (
        pageNavigationState?.secondaryNavSlug === 'faqs' &&
        secondaryNavItems.find((item) => item.key === 'faqs')
      ) {
        onNavSelect('faqs');
        defer(() => {
          nav.assignFocus(
            `${secondaryNavId}-item-${secondaryNavItems.findIndex(
              (item) => item.key === 'faqs',
            )}`,
          );
        });
      } else {
        nav.assignFocus(secondaryNavId);
      }
      setLoadingIndicatorVisibility?.(false);
    }
  }, [secondaryNavItems]);

  useEffect(() => {
    if (deviceInformation?.ipAddress.ip || deviceInformation?.deviceId) {
      const appInfoDescription = ` App \n \n Version: v${version} \n Engine: Chromium \n Platform: ${getHbbTvPlatformVersion()} \n System Name: ${systemName} \n \n ${
        isLoggedIn()
          ? `User \n \n Email: ${email} \n Ip Address: ${deviceInformation.ipAddress.ip} \n State: ${activeRegion} \n \n`
          : ''
      } Device \n \n Manufacturer: ${
        deviceInformation.manufacturer
      } \n  Model: ${deviceInformation.model} \n Device ID: ${
        deviceInformation.deviceId
      } \n OS Version: ${deviceInformation.firmwareVersion}`;
      setAppInformation(appInfoDescription);
      const navItems = getNavItems(settingsNavItems);
      setSecondaryNavItems(navItems);
    }
  }, [deviceInformation]);

  useKeyPress('BACK', (): boolean => {
    const { parent } = nav.currentFocusNode || {};

    if (parent === REGION_NAV_LIST_ID) {
      safeAssignFocus(secondaryNavId);

      return true;
    }

    return onBackPressOnPagesWithSideNav(parent || '', secondaryNavId);
  });

  return (
    <div className={styles.settingsPageContainer}>
      <Nav.Horizontal id="settings-page-nav">
        <SecondaryNav
          items={secondaryNavItems}
          title={'settings'.toUpperCase()}
          navId={secondaryNavId}
          hidden={false}
          onSelect={onNavSelect}
          activeKey={currentNavSetting}
        />
        {currentNavSetting === 'change-region' ? (
          <div className={styles.selectContainer}>
            <Nav.Vertical id={REGION_NAV_LIST_ID}>
              <div className={styles.header}>Select Your Region</div>
              {regions.map((item: string) => {
                const itemId = `region-item-${item}`;
                return (
                  <div id={itemId} key={itemId}>
                    <Nav.Focusable
                      id={itemId}
                      onSelect={() => onRegionOptionSelect(item)}
                    >
                      <div className={styles.regionItem}>
                        {activeRegion === item ? (
                          <ActiveRadioButton className={styles.radioButton} />
                        ) : (
                          <RadioButton className={styles.radioButton} />
                        )}
                        {item}
                      </div>
                    </Nav.Focusable>
                  </div>
                );
              })}
            </Nav.Vertical>
          </div>
        ) : (
          <div>
            <div className={styles.staticPage}>
              <div className={styles.pageLabel}>{settingsData?.label}</div>
              <div className={styles.pageContent}>
                {settingsData?.description?.replace(/\r\n/g, '\n') || ''}
              </div>
            </div>
          </div>
        )}
      </Nav.Horizontal>
    </div>
  );
};
